import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import '../styles/pages/HomeExperience.css';

const industries = [
  { number: '01', name: 'Commercial life sciences', detail: 'Distribution feeds, customer mastering, specialty data, and governed commercial reporting.' },
  { number: '02', name: 'EdTech & learning platforms', detail: 'Learner progression, curriculum telemetry, institutional analytics, and AI-enabled learning tools.' },
  { number: '03', name: 'Cybersecurity & developer systems', detail: 'Repository posture, dependency telemetry, audit trails, and bounded security automation.' },
  { number: '04', name: 'Fitness, health & consumer apps', detail: 'Connected device signals, on-device vision and workout logs, member analytics, and mobile foundations.' },
];
const capabilities = [
  { number: '01', kicker: '01 / Reconcile', tags: ['Unified model', 'Master data'], title: 'A consistent operational view', detail: 'Bring source pipelines, product telemetry, and master data into one agreed model.' },
  { number: '02', kicker: '02 / Govern', tags: ['Traceability', 'Quality gates'], title: 'Quality you can inspect', detail: 'Trace data from source to report, with reconciliation rules and monitored exceptions.' },
  { number: '03', kicker: '03 / Activate', tags: ['Governed access', 'Operational handover'], title: 'Ready for analytics and AI', detail: 'Publish governed data products your team can understand and operate.' },
];

const blueprintNotes = [
  {
    code: 'ZONE 01 // INGESTION CONDUITS',
    title: 'Standardize raw source feeds',
    detail: 'Bring application telemetry, partner feeds, operational databases, and CRM streams into a Bronze staging layer with automated schema and freshness checks on arrival.',
  },
  {
    code: 'ZONE 02 // RESOLUTION & UNITY CATALOG',
    title: 'Resolve core business entities',
    detail: 'Match accounts, users, products, and events against agreed mastering rules, route exceptions for review, and enforce Unity Catalog permissions.',
  },
  {
    code: 'ZONE 03 // GOVERNED ACTIVATION',
    title: 'Publish for reporting and AI',
    detail: 'Deliver Silver and Gold tables with documented lineage so leadership, operations, and AI workflows query the same verified numbers.',
  },
];

const cadenceStages = [
  {
    weeks: 'WEEKS 1–2',
    label: 'BLUEPRINT',
    title: 'Diagnostic & architecture blueprint',
    summary: 'We inventory active source feeds, measure baseline data quality, and lock the target Databricks schema and acceptance thresholds before pipeline build begins.',
    gate: 'GATE 01 // Read-only source access, baseline measurements & signed acceptance criteria.',
  },
  {
    weeks: 'WEEKS 3–6',
    label: 'LAKEHOUSE',
    title: 'Core lakehouse pipeline build',
    summary: 'We engineer Bronze, Silver, and Gold Delta layers, standardize schemas, configure Unity Catalog governance, and wire automated deployment tests.',
    gate: 'GATE 02 // Reviewed deployment code, repeatable CI/CD runs & tested source-to-target mappings.',
  },
  {
    weeks: 'WEEKS 7–10',
    label: 'RECONCILE',
    title: 'Reconciliation & observability',
    summary: 'We implement entity matching across customer, user, and product masters, stand up exception queues, and attach freshness and volume gates ahead of reporting.',
    gate: 'GATE 03 // Measured match rates, traceable exception queues & tested alerts with named owners.',
  },
  {
    weeks: 'WEEKS 11–12',
    label: 'HANDOVER',
    title: 'Activation & team handover',
    summary: 'We publish agreed reporting tables and semantic views, complete stakeholder validation through Day 90, and hand over runbooks and operational ownership.',
    gate: 'GATE 04 // Signed Day 90 acceptance evidence pack & full operational handover to your team.',
  },
];

const ConstructivistMark = ({ index = 0 }) => {
  if (index === 0) {
    return (
      <svg className="capability-iso-mark" viewBox="0 0 56 56" aria-hidden="true">
        <polygon points="28,4 50,17 50,39 28,52 6,39 6,17" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <line x1="6" y1="17" x2="50" y2="39" stroke="currentColor" strokeWidth="1" />
        <line x1="50" y1="17" x2="6" y2="39" stroke="currentColor" strokeWidth="1" />
        <line x1="28" y1="4" x2="28" y2="52" stroke="currentColor" strokeWidth="1.2" />
        <polygon points="28,16 38,22 38,34 28,40 18,34 18,22" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2" />
        <rect x="24.5" y="24.5" width="7" height="7" fill="var(--red)" />
      </svg>
    );
  }
  if (index === 1) {
    return (
      <svg className="capability-iso-mark" viewBox="0 0 56 56" aria-hidden="true">
        <polygon points="28,4 50,17 50,39 28,52 6,39 6,17" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <polygon points="28,13 42,21 42,35 28,43 14,35 14,21" fill="none" stroke="currentColor" strokeWidth="1.35" />
        <line x1="28" y1="4" x2="28" y2="13" stroke="currentColor" strokeWidth="1" />
        <line x1="50" y1="17" x2="42" y2="21" stroke="currentColor" strokeWidth="1" />
        <line x1="50" y1="39" x2="42" y2="35" stroke="currentColor" strokeWidth="1" />
        <line x1="28" y1="52" x2="28" y2="43" stroke="currentColor" strokeWidth="1" />
        <line x1="6" y1="39" x2="14" y2="35" stroke="currentColor" strokeWidth="1" />
        <line x1="6" y1="17" x2="14" y2="21" stroke="currentColor" strokeWidth="1" />
        <polyline points="14,21 28,28 42,21" fill="none" stroke="var(--red)" strokeWidth="1.75" />
        <line x1="28" y1="28" x2="28" y2="43" stroke="var(--red)" strokeWidth="1.75" />
      </svg>
    );
  }
  return (
    <svg className="capability-iso-mark" viewBox="0 0 56 56" aria-hidden="true">
      <polygon points="28,4 50,17 50,39 28,52 6,39 6,17" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <polyline points="6,17 28,28 50,17" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <line x1="28" y1="28" x2="28" y2="52" stroke="currentColor" strokeWidth="1.2" />
      <polygon points="28,17 39,23.5 28,30 17,23.5" fill="var(--red)" />
      <polyline points="17,33 28,39.5 39,33" fill="none" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
};

const SystemBlueprintGraphic = () => (
  <figure className="home-schematic__figure" aria-label="Data foundation schematic: ERP, Telemetry, Partner APIs, and CRM feeds enter an isometric ingestion core, pass through the Entity Resolution and Unity Catalog gate, and publish to Bronze/Silver/Gold layers, Governed Reporting, and AI Readiness">
    <svg
      className="home-schematic__svg"
      viewBox="0 0 1360 440"
      role="img"
      aria-hidden="true"
    >
      {/* ─── BACKGROUND DRAFTING GRID ─── */}
      <g className="home-schematic__grid">
        {[72, 168, 224, 340, 462, 578, 694, 810, 932, 1060, 1188, 1264, 1312].map((x) => (
          <line key={x} x1={x} y1="0" x2={x} y2="440" />
        ))}
        <line x1="0" y1="42" x2="1360" y2="42" />
        <line x1="0" y1="398" x2="1360" y2="398" />
      </g>

      {/* ─── LEFT: MULTI-LINE SOURCE CONDUITS (ERP, TELEMETRY, APIs, CRM) ─── */}
      <g className="home-schematic__traces">
        {[0, 8, 16, 24].map((offset, i) => (
          <polyline
            key={`erp-${i}`}
            points={`0,${68 + offset} 224,${68 + offset} 308,${152 + i * 7} 400,${152 + i * 7}`}
          />
        ))}
        {[0, 8, 16, 24].map((offset, i) => (
          <polyline
            key={`telemetry-${i}`}
            points={`0,${150 + offset} 224,${150 + offset} 308,${184 + i * 7} 400,${184 + i * 7}`}
          />
        ))}
        {[0, 8, 16, 24].map((offset, i) => (
          <polyline
            key={`apis-${i}`}
            points={`0,${234 + offset} 224,${234 + offset} 308,${216 + i * 7} 400,${216 + i * 7}`}
          />
        ))}
        {[0, 8, 16, 24].map((offset, i) => (
          <polyline
            key={`crm-${i}`}
            points={`0,${318 + offset} 224,${318 + offset} 308,${248 + i * 7} 400,${248 + i * 7}`}
          />
        ))}
      </g>

      {/* Source Vertical Collector Bars & Ticks */}
      <g className="home-schematic__calipers">
        <line x1="184" y1="52" x2="184" y2="364" />
        <line x1="178" y1="52" x2="190" y2="52" />
        <line x1="178" y1="364" x2="190" y2="364" />
        <line x1="204" y1="62" x2="204" y2="354" />
        <line x1="199" y1="62" x2="209" y2="62" />
        <line x1="199" y1="354" x2="209" y2="354" />
      </g>

      {/* Source Feed Labels */}
      <g className="home-schematic__source-labels">
        <rect x="64" y="69" width="62" height="22" fill="#FFFFFF" />
        <text x="72" y="85">ERP</text>

        <rect x="64" y="151" width="98" height="22" fill="#FFFFFF" />
        <text x="72" y="167">TELEMETRY</text>

        <rect x="64" y="235" width="96" height="22" fill="#FFFFFF" />
        <text x="72" y="251">APIs &amp; EDI</text>

        <rect x="64" y="319" width="96" height="22" fill="#FFFFFF" />
        <text x="72" y="335">CRM &amp; APP</text>
      </g>

      {/* ─── CENTER-LEFT: ISOMETRIC BLACK CONSTRUCTIVIST MONOLITH ─── */}
      {/* Connecting lines between Black Monolith and Red Arch */}
      <g className="home-schematic__bridge-lines">
        {[162, 178, 194, 210, 226, 242, 258].map((y) => (
          <line key={y} x1="540" y1={y} x2="646" y2={y} />
        ))}
      </g>

      <g className="home-schematic__monolith" stroke="#FFFFFF" strokeWidth="1.6" strokeLinejoin="miter">
        {/* Base Pillar & Lower Isometric Blocks */}
        <polygon points="462,250 548,202 548,326 462,374" fill="#0B0B0C" />
        <polygon points="376,298 462,250 462,374 376,326" fill="#0B0B0C" />
        <polygon points="376,272 418,248 462,274 420,298" fill="#18181A" />

        {/* Middle Interlocking Step */}
        <polygon points="376,188 462,236 462,282 376,234" fill="#0B0B0C" />
        <polygon points="462,236 548,188 548,234 462,282" fill="#0B0B0C" />
        <polygon points="376,188 424,160 510,208 462,236" fill="#18181A" />

        {/* Upper Isometric Crown Block */}
        <polygon points="376,102 462,54 548,102 462,150" fill="#0B0B0C" />
        <polygon points="376,102 462,150 462,198 376,150" fill="#0B0B0C" />
        <polygon points="462,150 548,102 548,150 462,198" fill="#18181A" />

        {/* Cutout Structural Slot Detail */}
        <polyline points="502,228 502,352" fill="none" stroke="#FFFFFF" strokeWidth="1.6" />
        <polyline points="484,268 524,246 524,340" fill="none" stroke="#FFFFFF" strokeWidth="1.6" />
      </g>

      {/* ─── CENTER-RIGHT: VERMILLION RED ISOMETRIC ARCH GATE ─── */}
      <g className="home-schematic__arch" stroke="#0B0B0C" strokeWidth="1.8" strokeLinejoin="miter">
        {/* Back Right Column of Arch */}
        <polygon points="782,118 814,100 814,304 782,322" fill="#E10600" />
        <polygon points="754,102 782,118 782,322 754,306" fill="#B80500" />

        {/* Top Isometric Crossbeam of Arch */}
        <polygon points="632,148 782,64 814,82 664,166" fill="#E10600" />
        <polygon points="664,166 814,82 814,122 664,206" fill="#C90500" />

        {/* Front Left Column of Arch */}
        <polygon points="632,148 664,166 664,378 632,360" fill="#B80500" />
        <polygon points="664,166 698,147 698,359 664,378" fill="#E10600" />
      </g>

      {/* Gate Technical Plate Label */}
      <g className="home-schematic__gate-plate">
        <rect x="682" y="212" width="198" height="58" fill="#FFFFFF" />
        <line x1="682" y1="212" x2="880" y2="212" stroke="#0B0B0C" strokeWidth="1.5" />
        <line x1="682" y1="270" x2="880" y2="270" stroke="#0B0B0C" strokeWidth="1.5" />
        <text x="692" y="236" className="home-schematic__gate-title">ENTITY RESOLUTION</text>
        <text x="692" y="256" className="home-schematic__gate-title">&amp; UNITY CATALOG</text>
      </g>

      {/* ─── RIGHT: THREE PARALLEL GOVERNED OUTPUT TRACKS ─── */}
      <g className="home-schematic__outputs">
        {/* Track 1: BRONZE / SILVER / GOLD */}
        <polyline points="814,186 882,186 932,110 1284,110" />
        <polyline points="814,198 890,198 940,136 1284,136" />
        <line x1="932" y1="150" x2="1284" y2="150" />
        <text x="954" y="130" className="home-schematic__track-label">BRONZE / SILVER / GOLD</text>

        {/* Track 2: GOVERNED REPORTING */}
        <polyline points="884,226 932,202 1284,202" />
        <polyline points="884,242 932,228 1284,228" />
        <line x1="932" y1="242" x2="1284" y2="242" />
        <text x="954" y="222" className="home-schematic__track-label">GOVERNED REPORTING</text>

        {/* Track 3: AI READINESS */}
        <polyline points="814,284 888,284 932,294 1284,294" />
        <polyline points="814,296 880,296 932,320 1284,320" />
        <line x1="932" y1="334" x2="1284" y2="334" />
        <text x="954" y="314" className="home-schematic__track-label">AI READINESS</text>
      </g>

      {/* Right-Edge Engineering Dimension Callouts */}
      <g className="home-schematic__dimensions">
        {/* Track 1 Callout */}
        <line x1="1272" y1="110" x2="1272" y2="150" />
        <polygon points="1272,110 1269,117 1275,117" fill="#0B0B0C" />
        <polygon points="1272,150 1269,143 1275,143" fill="#0B0B0C" />
        <text x="1290" y="134">L1–L3</text>

        {/* Track 2 Callout */}
        <line x1="1272" y1="202" x2="1272" y2="242" />
        <polygon points="1272,202 1269,209 1275,209" fill="#0B0B0C" />
        <polygon points="1272,242 1269,235 1275,235" fill="#0B0B0C" />
        <text x="1290" y="226">SLA</text>

        {/* Track 3 Callout */}
        <line x1="1272" y1="294" x2="1272" y2="334" />
        <polygon points="1272,294 1269,301 1275,301" fill="#0B0B0C" />
        <polygon points="1272,334 1269,327 1275,327" fill="#0B0B0C" />
        <text x="1290" y="318">GOV</text>
      </g>
    </svg>
  </figure>
);

const CadenceTimelineGraphic = () => {
  const [activeStage, setActiveStage] = useState(0);

  return (
    <div className="home-cadence-bar">
      <div className="home-cadence-bar__columns" role="group" aria-label="90-day delivery stages">
        {cadenceStages.map((stage, i) => (
          <button
            type="button"
            key={stage.label}
            className={`home-cadence-bar__col${activeStage === i ? ' is-active' : ''}`}
            aria-pressed={activeStage === i}
            onClick={() => setActiveStage(i)}
            onMouseEnter={() => setActiveStage(i)}
          >
            <span className="home-cadence-bar__weeks">{stage.weeks}</span>
            <strong className="home-cadence-bar__title">{stage.label}</strong>
            <span className="home-cadence-bar__ticks" aria-hidden="true" />
          </button>
        ))}
      </div>

      <div className="home-cadence-bar__beam-wrap">
        <svg
          className="home-cadence-bar__svg"
          viewBox="0 0 1360 116"
          role="img"
          aria-label="Stepped 90-day architectural cadence beam culminating at Day 90"
        >
          {/* Stage 1 High Horizontal Beam */}
          <rect x="0" y="18" width="290" height="32" fill="#0B0B0C" />
          {/* Red Milestone Cube 1 */}
          <rect x="242" y="30" width="48" height="38" fill="#E10600" stroke="#FFFFFF" strokeWidth="1.5" />
          {/* Isometric Step Down 1 -> 2 */}
          <polygon points="290,18 336,48 336,80 290,50" fill="#18181A" stroke="#FFFFFF" strokeWidth="1.2" />
          <polygon points="290,50 336,80 290,80" fill="#0B0B0C" />

          {/* Stage 2 Low Horizontal Beam */}
          <rect x="336" y="48" width="290" height="32" fill="#0B0B0C" />
          {/* Red Milestone Cube 2 */}
          <rect x="578" y="28" width="48" height="36" fill="#E10600" stroke="#FFFFFF" strokeWidth="1.5" />
          <rect x="578" y="64" width="48" height="16" fill="#0B0B0C" stroke="#FFFFFF" strokeWidth="1.2" />
          {/* Isometric Step Up 2 -> 3 */}
          <polygon points="626,48 666,18 666,50 626,80" fill="#18181A" stroke="#FFFFFF" strokeWidth="1.2" />

          {/* Stage 3 High Horizontal Beam */}
          <rect x="666" y="18" width="296" height="32" fill="#0B0B0C" />
          {/* Red Milestone Cube 3 */}
          <rect x="914" y="30" width="48" height="38" fill="#E10600" stroke="#FFFFFF" strokeWidth="1.5" />
          {/* Isometric Connector 3 -> 4 */}
          <polygon points="962,30 982,18 982,50 962,68" fill="#18181A" stroke="#FFFFFF" strokeWidth="1.2" />

          {/* Stage 4 Final Horizontal Beam */}
          <rect x="982" y="18" width="266" height="32" fill="#0B0B0C" />

          {/* Active Stage Indicator Line */}
          <rect
            x={[0, 336, 666, 982][activeStage]}
            y="10"
            width={[290, 290, 296, 266][activeStage]}
            height="3"
            fill="#E10600"
          />

          {/* DAY 90 Solid Red Hanko Seal */}
          <g transform="translate(1248, 0)">
            <rect x="0" y="0" width="96" height="86" fill="#E10600" />
            <text x="48" y="39" textAnchor="middle" className="home-cadence-bar__seal-top">DAY</text>
            <text x="48" y="69" textAnchor="middle" className="home-cadence-bar__seal-num">90</text>
          </g>
        </svg>
      </div>

      <div className="home-cadence-bar__detail" aria-live="polite">
        <div className="home-cadence-bar__prose">
          <span className="home-cadence-bar__eyebrow">{cadenceStages[activeStage].weeks} · STAGE 0{activeStage + 1}</span>
          <h3>{cadenceStages[activeStage].title}</h3>
          <p>{cadenceStages[activeStage].summary}</p>
        </div>
        <div className="home-cadence-bar__assurance">
          <p className="home-cadence-bar__gate">
            <span className="seal seal--sm" aria-hidden="true" />
            <span>{cadenceStages[activeStage].gate}</span>
          </p>
          <Link className="home-text-link" to="/solutions">
            Inspect acceptance gates &amp; milestone assurance <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

const EngagementMonolithMark = () => (
  <div className="home-fit__monolith">
    <svg viewBox="0 0 120 180" className="home-fit__monolith-svg" aria-hidden="true">
      {/* Solid Black Isometric Tower with White Architectural Wireframe */}
      <polygon points="60,8 108,32 108,148 60,172 12,148 12,32" fill="#0B0B0C" />
      <polyline points="12,32 60,56 108,32" fill="none" stroke="#FFFFFF" strokeWidth="1.5" />
      <line x1="60" y1="8" x2="60" y2="172" stroke="#FFFFFF" strokeWidth="1.5" />
      <polygon points="60,26 92,42 92,138 60,154 28,138 28,42" fill="none" stroke="#FFFFFF" strokeWidth="1.2" />
      {/* Lower Geodesic / Wireframe Base */}
      <polyline points="12,116 60,140 108,116" fill="none" stroke="#FFFFFF" strokeWidth="1.2" />
      <line x1="12" y1="116" x2="60" y2="172" stroke="#FFFFFF" strokeWidth="1" />
      <line x1="108" y1="116" x2="60" y2="172" stroke="#FFFFFF" strokeWidth="1" />
      <line x1="12" y1="148" x2="60" y2="116" stroke="#FFFFFF" strokeWidth="1" />
      <line x1="108" y1="148" x2="60" y2="116" stroke="#FFFFFF" strokeWidth="1" />
      {/* Vermillion Seal Accent */}
      <rect x="53" y="49" width="14" height="14" fill="#E10600" />
    </svg>
    <p className="home-fit__monolith-note">
      Defined scope, milestone-tied fees, and a documented Day 90 acceptance pack your engineering team owns from day one.
    </p>
  </div>
);

const Home = () => (
  <div className="page home-experience">
    <SEO
      title="Commercial Data Foundations on Databricks"
      description="A focused 90-day engagement for an auditable, analytics-ready commercial data foundation: Databricks, Unity Catalog, reconciliation, and operational handover."
      path="/"
    />

    <main>
      <section className="home-hero" id="top" aria-labelledby="home-hero-title">
        <div className="home-hero__copy">
          <p className="home-hero__descriptor">Commercial data · AI readiness</p>
          <h1 className="kinetic-title" id="home-hero-title">We build<em>what works.</em></h1>
          <p className="home-hero__lede">A 90-day engagement to unify fragmented pipelines and inconsistent master data into an auditable, analytics-ready Databricks foundation. Defined scope. Measured acceptance. A team ready to run it.</p>
          <div className="home-hero__actions">
            <Link className="btn btn--primary" to="/contact#inquiry">Discuss your data foundation <span aria-hidden="true">↗</span></Link>
            <Link className="home-text-link home-hero__secondary" to="/solutions">See our approach <span aria-hidden="true">↗</span></Link>
          </div>
        </div>
        <div className="home-hero__art">
          <img className="home-hero__artwork" src="/images/aurablox-constructivist.png" alt="Abstract AURABLOX architectural geometry in black, white, and red" fetchpriority="high" />
        </div>
      </section>

      <section className="home-section home-capabilities" id="capabilities" aria-labelledby="capabilities-heading">
        <div className="section-index-bar" aria-hidden="true">
          <span className="section-index-bar__left"><span className="seal seal--sm" /> 01 // COMMERCIAL DATA</span>
          <span className="section-index-bar__right">RECONCILE · GOVERN · ACTIVATE</span>
        </div>
        <div className="home-section__heading">
          <div>
            <h2 id="capabilities-heading">What changes for your team</h2>
            <p className="home-section__sub">A focused commercial data foundation, built around the decisions your team needs to make.</p>
          </div>
          <Link className="home-text-link home-capabilities__link" to="/solutions">Explore the 90-day engagement <span aria-hidden="true">↗</span></Link>
        </div>
        <div className="home-capability-grid">
          {capabilities.map((capability, i) => (
            <article className="home-capability" key={capability.number}>
              <div className="home-capability__top">
                <span className="home-capability__kicker">{capability.kicker}</span>
                <ConstructivistMark index={i} />
              </div>
              <h3>{capability.title}</h3>
              <p>{capability.detail}</p>
              <ul className="home-capability__tags" aria-label={`${capability.title} focus areas`}>
                {capability.tags.map((tag) => (
                  <li key={tag}>{tag}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="home-section home-schematic" id="system-blueprint" aria-labelledby="blueprint-heading">
        <div className="section-index-bar" aria-hidden="true">
          <span className="section-index-bar__left"><span className="seal seal--sm" /> 02 // SYSTEM BLUEPRINT</span>
          <span className="section-index-bar__right">INGEST · RESOLVE · PUBLISH</span>
        </div>
        <div className="home-section__heading">
          <div>
            <h2 id="blueprint-heading">From fragmented feeds to governed tables</h2>
            <p className="home-section__sub">
              Teams across every industry lose weeks reconciling numbers across disconnected vendors, product databases, and internal systems. We engineer a single lakehouse pipeline that validates raw feeds on arrival, resolves shared identities, and publishes trusted tables with full lineage.
            </p>
          </div>
          <Link className="home-text-link" to="/technology">Inspect engineering stack <span aria-hidden="true">↗</span></Link>
        </div>
        <SystemBlueprintGraphic />
        <div className="home-schematic__notes">
          {blueprintNotes.map((note) => (
            <article className="home-schematic__note" key={note.code}>
              <span className="home-schematic__note-code">{note.code}</span>
              <h3>{note.title}</h3>
              <p>{note.detail}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="home-section home-cadence" id="delivery-cadence" aria-labelledby="cadence-heading">
        <div className="section-index-bar" aria-hidden="true">
          <span className="section-index-bar__left"><span className="seal seal--sm" /> 03 // 90-DAY CADENCE</span>
          <span className="section-index-bar__right">FOUR STAGES · MEASURED ACCEPTANCE</span>
        </div>
        <div className="home-section__heading">
          <div>
            <h2 id="cadence-heading">Four stages. Ninety days to handover.</h2>
            <p className="home-section__sub">
              Every engagement runs against a fixed 12-week delivery plan within a 90-calendar-day window, reserving Days 85–90 for final verification. Each stage closes with an agreed acceptance gate and measurable quality evidence.
            </p>
          </div>
          <Link className="home-text-link" to="/contact#inquiry">Scope your 90-day build <span aria-hidden="true">↗</span></Link>
        </div>
        <CadenceTimelineGraphic />
      </section>

      <section className="home-industries" id="industries" aria-labelledby="industries-heading">
        <div className="home-industries__frame">
          <div className="section-index-bar" aria-hidden="true">
            <span className="section-index-bar__left"><span className="seal seal--sm" /> 04 // SECTORS &amp; ENGAGEMENT FIT</span>
            <span className="section-index-bar__right">ALL INDUSTRIES · DEFINED SCOPE</span>
          </div>
          <div className="home-fit">
            <div className="home-industries__header">
              <h2 id="industries-heading">Where we focus</h2>
              <p>We serve organizations across industries—bringing experience across commercial life sciences, EdTech, cybersecurity, and fitness &amp; health—when a reporting, product, or AI deadline requires a technical sponsor and a foundation built to last.</p>
              <EngagementMonolithMark />
            </div>
            <ol className="home-fit__list">
              {industries.map((industry) => (
                <li className="home-fit__item" key={industry.name}>
                  <span className="home-fit__number" aria-hidden="true">{industry.number}</span>
                  <h3>{industry.name}</h3>
                  <p>{industry.detail}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

    </main>
  </div>
);

export default Home;
